// src/careComponents/hooks/useSubmitInquiry.ts
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_REQUEST } from "../../graphql/mutations/care";
import { GET_MY_REFERRER_PROFILE } from "../../graphql/queries/care";
import { isOpenStatus } from "../utils/statuses";

/* =============================================================================
   Public types
   ========================================================================== */

export type RequestKind = "availability" | "referral" | "waitlist";
export type Urgency = "IMMEDIATE" | "WEEK" | "MONTH" | "UNKNOWN";
export type PayerType = "MEDICAID" | "MEDICARE" | "PRIVATE" | "VA" | "UNKNOWN";

export type ExistingInquiryLite = {
  id: string;
  viewerToken?: string | null;
  message?: string | null;

  createdAt?: string | number | null;
  desiredDate?: string | null;
  kind?: string | null;
  businessId?: number | null;
  status?: string | null;
  residentKey?: string | null;
};

export type DuplicateDecision =
  | "use-existing"
  | "fix-typo"
  | "different-resident"
  | "reuse"
  | "create_new"
  | "cancel";

export type SubmitInquiryInput = {
  businessId: number;
  kind: RequestKind;
  name: string;
  orgName?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
  desiredDate?: string | null;
  residentKey?: string | null;
  extra?: string | null;
  urgency?: Urgency;
  payerType?: PayerType;
  referralSourceTypeId?: number | null; // GraphQL Int
};

type SubmitResult = {
  ok: boolean;
  reused?: boolean;
  id?: string | null;
  message?: string | null;
  viewerToken?: string | null;
  trackUrl?: string | null;
};

type UseSubmitInquiryOptions = {
  onDuplicate?: (args: {
    id: string;
    viewerToken?: string | null;
    message?: string | null;
  }) => void;

  onDuplicateLegacy?: (
    existing: ExistingInquiryLite,
    input: SubmitInquiryInput
  ) => Promise<DuplicateDecision> | DuplicateDecision;

  passResidentKeyToCheck?: boolean; // kept for compatibility
};

function normalizeDecision(
  d: DuplicateDecision
): "reuse" | "create_new" | "cancel" {
  switch (d) {
    case "use-existing":
    case "reuse":
      return "reuse";
    case "different-resident":
    case "create_new":
      return "create_new";
    case "fix-typo":
    case "cancel":
    default:
      return "cancel";
  }
}

/* =============================================================================
   Local helpers
   ========================================================================== */

const norm = (s?: string | null) => (s ?? "").trim().toUpperCase();

/** Only duplicate when same provider + same residentKey + open status. */
function findSameResidentDuplicate(
  myLeads: ExistingInquiryLite[] | undefined,
  input: SubmitInquiryInput
): ExistingInquiryLite | null {
  if (!myLeads || !Array.isArray(myLeads)) return null;

  const rkNew = norm(input.residentKey);
  if (!rkNew) return null; // cannot safely dedupe without resident key

  const match = myLeads.find((l) => {
    if (Number(l.businessId) !== Number(input.businessId)) return false;
    if (!l.status || !isOpenStatus(l.status)) return false;
    return norm(l.residentKey) === rkNew;
  });

  return match || null;
}

/* =============================================================================
   Hook
   ========================================================================== */

export function useSubmitInquiry(opts?: UseSubmitInquiryOptions) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [trackUrl, setTrackUrl] = useState<string | null>(null);

  // 🔹 bring back pending so callers depending on it compile & can show dialogs
  const [pending, setPending] = useState<null | {
    type: "duplicate";
    existing: ExistingInquiryLite;
    input: SubmitInquiryInput;
  }>(null);

  // For client-side duplicate pre-check
  const { data: meData } = useQuery(GET_MY_REFERRER_PROFILE, {
    fetchPolicy: "cache-first",
  });
  const myLeads: ExistingInquiryLite[] =
    (meData?.myReferrerProfile?.referralLead as ExistingInquiryLite[]) ?? [];

  const [mutate] = useMutation(CREATE_REQUEST);

  const actuallySubmit = useCallback(
    async (input: SubmitInquiryInput): Promise<SubmitResult> => {
      const variables = {
        input: {
          businessId: input.businessId,
          kind: input.kind,
          name: input.name,
          orgName: input.orgName ?? null,
          email: input.email ?? null,
          phone: input.phone ?? null,
          notes: input.notes ?? null,
          desiredDate: input.desiredDate ?? null,
          extra: input.extra ?? null,
          residentKey: input.residentKey ?? null,
          urgency: input.urgency ?? "UNKNOWN",
          referralSourceTypeId: input.referralSourceTypeId ?? null,
        },
      };

      const { data } = await mutate({ variables });
      const res = data?.createRequest;

      // Fallback: backend duplicates
      if (!res?.success && res?.id) {
        const existing: ExistingInquiryLite = {
          id: String(res.id),
          viewerToken: res.viewerToken ?? null,
          message: res.message ?? null,
          residentKey: res.residentKey ?? null,
          createdAt: res.createdAt ?? null,
          desiredDate: res.desiredDate ?? null,
          kind: res.kind ?? null,
          businessId: res.businessId ?? null,
          status: res.status ?? null,
        };

        // Surface as pending duplicate for any legacy UIs watching this flag
        setPending({ type: "duplicate", existing, input });

        if (opts?.onDuplicateLegacy) {
          const raw = await opts.onDuplicateLegacy(existing, input);
          const decision = normalizeDecision(raw);
          // Clear pending after decision
          setPending(null);

          return {
            ok: false,
            reused: decision === "reuse",
            id: existing.id,
            message: res.message,
            viewerToken: res.viewerToken,
          };
        }

        if (opts?.onDuplicate) {
          opts.onDuplicate({
            id: existing.id,
            viewerToken: existing.viewerToken,
            message: existing.message,
          });
        }

        // keep pending set; caller might close dialog themselves
        return {
          ok: false,
          reused: true,
          id: existing.id,
          message: res.message,
          viewerToken: res.viewerToken,
        };
      }

      if (!res?.success) {
        return { ok: false, message: res?.message || "Failed to submit." };
      }

      const url = res.viewerToken
        ? `/referrer/portal/${res.viewerToken}`
        : null;

      return {
        ok: true,
        id: res.id,
        message: res.message,
        viewerToken: res.viewerToken,
        trackUrl: url,
      };
    },
    [mutate, opts]
  );

  const submit = useCallback(
    async (input: SubmitInquiryInput): Promise<SubmitResult> => {
      setError(null);
      setSubmitting(true);
      setToast(null);
      setTrackUrl(null);
      setPending(null);

      try {
        // 1) Client-side pre-check (same provider + same residentKey + open status)
        const existing = findSameResidentDuplicate(myLeads, input);

        if (existing) {
          // expose pending for any UIs still relying on it
          setPending({ type: "duplicate", existing, input });

          if (opts?.onDuplicateLegacy) {
            const raw = await opts.onDuplicateLegacy(existing, input);
            const decision = normalizeDecision(raw);

            // decision handled; clear pending so dialog can close
            setPending(null);

            if (decision === "reuse") {
              return {
                ok: false,
                reused: true,
                id: existing.id,
                viewerToken: existing.viewerToken,
                message: existing.message,
              };
            }
            if (decision === "cancel") {
              return { ok: false, message: "Cancelled by user." };
            }
            // decision === "create_new": fall through to submit
          } else if (opts?.onDuplicate) {
            opts.onDuplicate({
              id: existing.id,
              viewerToken: existing.viewerToken,
              message: existing.message,
            });
            // keep pending set for any legacy UI watching it; also return
            return {
              ok: false,
              reused: true,
              id: existing.id,
              viewerToken: existing.viewerToken,
              message: existing.message,
            };
          }
        }

        // 2) Proceed with actual submission
        const result = await actuallySubmit(input);

        if (result.ok) {
          setToast("Request submitted.");
          if (result.trackUrl) setTrackUrl(result.trackUrl);
          setPending(null);
        } else if (result.message) {
          setError(result.message);
        }

        return result;
      } catch (e: any) {
        const msg = e?.message || "Network error.";
        setError(msg);
        return { ok: false, message: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [myLeads, opts, actuallySubmit]
  );

  const clearToast = () => setToast(null);

  return {
    submit,
    submitting,
    error,
    toast,
    clearToast,
    pending, // 🔹 restored
    trackUrl, // consumers can show a “track” link if needed
  };
}
